import React from 'react';
import { isEmpty, isEqual, map } from 'lodash';
import PropTypes from 'prop-types';
import { Button, Row, Col, Form, Modal, ProgressBar, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  ANNOTATION_AI_ENTITIES_GROUPS,
  ANNOTATION_AI_ENTITIES,
  getEntitiesForGroup
} from '../../../api/Annotations/Annotations';
import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';
import * as videoOps from '../../../api/Videos/video-operations';

import PageHeader from '../../atoms/PageHeader';

const VideoTemplateList = ({ video, templateForAI, changeVideoContainerStateProperty }) =>
  map(videoOps.getAnnotationTemplates(video), template => {
    const buttonStyle = isEqual(template, templateForAI) ? 'success' : 'light';
    return (
      <ListGroup.Item
        key={`template-${templateOps.getName(template)}`}
        id={`template-${templateOps.getName(template)}`}
        action
        variant={buttonStyle}
        onClick={() => {
          changeVideoContainerStateProperty('templateForAI', template);
        }}
        className="d-flex justify-content-between align-items-center"
      >
        <FontAwesomeIcon icon={templateOps.getIcon(template)} size="lg" />
        <span className="hidden-xs text-break">{templateOps.getName(template)}</span>
      </ListGroup.Item>
    );
  });

VideoTemplateList.propTypes = {
  video: PropTypes.object.isRequired,
  templateForAI: PropTypes.object.isRequired,
  changeVideoContainerStateProperty: PropTypes.func.isRequired
};

const AIAnnotationsModal = ({
  aiRequestOngoing,
  aiRequestProgress,
  selectedIndexes,
  showIAModal,
  templateForAI,
  handleCloseAIModal,
  handleRequestAIAnnotations,
  handleSelectAIIndex,
  ...rest
}) => {
  const disableAIModalAcceptButton =
    selectedIndexes.size === 0 || isEmpty(templateForAI) || aiRequestOngoing;
  const progressBarStyle = aiRequestProgress < 100 ? 'warning' : 'success';

  return (
    <Modal show={showIAModal} onHide={handleCloseAIModal} scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Automatic detection of items</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {aiRequestOngoing && (
          <Row>
            <Col xs={12}>
              <ProgressBar
                now={aiRequestProgress}
                label={`${aiRequestProgress}%`}
                variant={progressBarStyle}
                animated={aiRequestProgress < 100}
              />
            </Col>
          </Row>
        )}
        <Row noGutters>
          <Col xs={6} className="pr-2">
            <PageHeader>
              <h6>Select a template</h6>
            </PageHeader>
            <ListGroup variant="flush">
              <VideoTemplateList templateForAI={templateForAI} {...rest} />
            </ListGroup>
          </Col>
          <Col xs={6}>
            <PageHeader>
              <h6>Select items to detect</h6>
            </PageHeader>
            {map(ANNOTATION_AI_ENTITIES_GROUPS.properties, entitiesGroup => (
              <Form.Group key={entitiesGroup.name}>
                <Form.Label>{entitiesGroup.humanValue}</Form.Label>
                {map(getEntitiesForGroup(entitiesGroup.value), (value, key) => (
                  <Form.Check
                    key={`${ANNOTATION_AI_ENTITIES.properties[key].name}`}
                    type="checkbox"
                    id={`${value}-checkbox`}
                    value={value}
                    label={ANNOTATION_AI_ENTITIES.properties[key].humanValue}
                    onClick={handleSelectAIIndex}
                  />
                ))}
              </Form.Group>
            ))}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={handleCloseAIModal}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={disableAIModalAcceptButton}
          onClick={handleRequestAIAnnotations}
        >
          Start
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

AIAnnotationsModal.propTypes = {
  aiRequestOngoing: PropTypes.bool.isRequired,
  aiRequestProgress: PropTypes.number.isRequired,
  selectedIndexes: PropTypes.object.isRequired,
  showIAModal: PropTypes.bool.isRequired,
  templateForAI: PropTypes.object.isRequired,
  handleCloseAIModal: PropTypes.func.isRequired,
  handleRequestAIAnnotations: PropTypes.func.isRequired,
  handleSelectAIIndex: PropTypes.func.isRequired
};

export default AIAnnotationsModal;
